import { useUpdateUserMutation } from '@/state/mutations/users/use-update-user';
import { useGetOrganizationsQuery } from '@/state/queries/organizations/use-get-organizations.ts';
import { GET_USER_QUERY_KEY } from '@/state/queries/users/use-get-user';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import { type OrganizationDto } from '@/types/dtos/organizations/organization.dto';
import type { UserDto } from '@/types/dtos/user/response/user.dto';
import { ArrowForwardIos, Check, MoreHoriz } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  List as MuiList,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useNavigate } from 'react-router';

interface ListProps {
  close?: () => void;
}

export const List = ({ close }: ListProps) => {
  const { data, isError, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useGetOrganizationsQuery();
  const { mutate: updateUserMutation } = useUpdateUserMutation();
  const queryClient = useQueryClient();
  const { activeOrganization, setActiveOrganization } = useActiveOrganizationStore();
  const selectedRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  if (isError) {
    return <div>{error.message}</div>;
  }
  if (isLoading || !data)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
        <CircularProgress />
      </Box>
    );

  const listItemClick = (organization: OrganizationDto) => {
    if (organization.id !== activeOrganization?.id) {
      setActiveOrganization(organization);
      // Keep the user query aligned with the store immediately so SyncUserActiveOrganization
      // cannot treat the switch as a mismatch and pull the previous org back in.
      queryClient.setQueryData<UserDto>([GET_USER_QUERY_KEY], (prev) =>
        prev ? { ...prev, activeOrganization: organization.id } : prev,
      );
      updateUserMutation({
        activeOrganization: organization.id,
      });
      selectedRef.current?.scrollIntoView();
      const currentPath = window.location.pathname;
      const newPath = currentPath.includes('/organization/')
        ? currentPath.replace(/\/organization\/[^/]+/, `/organization/${organization.id}`)
        : `/organization/${organization.id}/events`;
      navigate(newPath, { replace: true });
      close?.();
    }
  };

  return (
    <Box
      sx={{
        p: 1,
        maxHeight: '500px',
        overflowY: 'scroll',
      }}
    >
      <MuiList>
        {activeOrganization && (
          <ListItemButton ref={selectedRef} autoFocus selected>
            <ListItemText primary={activeOrganization.name} />
            <ListItemIcon>
              <Check fontSize="small" />
            </ListItemIcon>
          </ListItemButton>
        )}
        {data.pages.map((page) =>
          page.data.map(
            (organization, i) =>
              organization.id !== activeOrganization?.id && (
                <ListItemButton key={`organization-list-item-${i}`} onClick={() => listItemClick(organization)}>
                  <ListItemText primary={organization.name} />
                  <ListItemIcon>
                    <ArrowForwardIos fontSize="small" />
                  </ListItemIcon>
                </ListItemButton>
              ),
          ),
        )}
      </MuiList>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <IconButton size="small" onClick={() => fetchNextPage()} disabled={!hasNextPage || isFetchingNextPage}>
          {isFetchingNextPage ? <CircularProgress /> : hasNextPage ? <MoreHoriz /> : null}
        </IconButton>
      </Box>
    </Box>
  );
};
